import type { UserInteractionSnapshot } from "@ai/context/AIContextEngineModel.SceneProjectUserContext";

export type AITranscriptInputType = "text" | "voice";

/**
 * Project/workflow state captured when a user turn begins. The answer keeps
 * this snapshot even if the user navigates before the agent finishes.
 */
export interface AITranscriptContext {
  readonly customerId: string;
  readonly projectId: string;
  readonly projectCustomerId: string;
  readonly userId: string;
  readonly userDisplayName: string;
  readonly workflowId: string | null;
  readonly stepId: string | null;
  readonly stepName: string | null;
  readonly sceneId: string | null;
  readonly agentRoleId: string;
  readonly modelId: string;
  readonly realtimeSessionId: string;
  readonly userInteractionState: UserInteractionSnapshot;
}

export interface AITranscriptRecallRequest {
  readonly customerId: string;
  readonly userId: string;
  readonly query: string | null;
  readonly matchType: "literal" | "similar";
  readonly sinceTimestamp: number;
  readonly untilTimestamp: number;
  readonly maxResults: number;
}

export interface AITranscriptRecallExchange {
  readonly exchangeId: string;
  readonly askedAt: number;
  readonly answeredAt: number | null;
  readonly question: string;
  readonly answer: string | null;
  readonly projectId: string;
  readonly workflowId: string | null;
  readonly stepId: string | null;
  readonly stepName: string | null;
  readonly sceneId: string | null;
  readonly inputType: AITranscriptInputType;
}

export interface AITranscriptRecallResult {
  readonly exchanges: ReadonlyArray<AITranscriptRecallExchange>;
  /** True when the read safety ceiling was reached before the whole window was inspected. */
  readonly readTruncated: boolean;
  /** Matching exchanges omitted because maxResults was reached. */
  readonly omittedMatchCount: number;
}

/**
 * Durable question/answer transcript sink. User and assistant messages are
 * separate turns joined by exchange_id/reply_to_turn_id, so an interrupted or
 * failed exchange still preserves the question that was asked.
 */
export interface IAITranscriptService {
  recordUserMessage(
    content: string,
    inputType: AITranscriptInputType,
    context: AITranscriptContext,
  ): void;
  recordAssistantMessage(content: string): void;
  recall(request: AITranscriptRecallRequest): Promise<AITranscriptRecallResult>;
  resetConversation(): void;
  flushPending(): Promise<void>;
  dispose(): Promise<void>;
}

export const AI_TRANSCRIPT_SERVICE = Symbol.for("Campfire.AITranscriptService");
